import { ActionType, type Condition, type ConditionAction, type ConditionGroup, type ConditionRule, type ConfiguratorOption, type ListOptionType, type PriceTable, RuleComparisonOperator, RuleValidationMode } from "@/internal/interfaces"

import { conditionActionSpec } from "#models/Configurator"
import { StringId } from "effect-app/Schema"

interface RuleComparisonOperatorSpec {
  operator: RuleComparisonOperator
  title: string
}

export function getRuleComparisonOperators(): RuleComparisonOperatorSpec[] {
  const operatorSpecs: RuleComparisonOperatorSpec[] = []

  for (const [, operator] of Object.entries(RuleComparisonOperator)) {
    const operatorSpec: RuleComparisonOperatorSpec = {
      operator,
      title: operator.toString()
    }

    switch (operator) {
      case RuleComparisonOperator.Equal:
        operatorSpec.title = "ist gleich"
        break

      case RuleComparisonOperator.NotEqual:
        operatorSpec.title = "ist nicht gleich"
        break

      case RuleComparisonOperator.Less:
        operatorSpec.title = "ist kleiner"
        break

      case RuleComparisonOperator.LessOrEqual:
        operatorSpec.title = "ist kleiner oder gleich"
        break

      case RuleComparisonOperator.Greater:
        operatorSpec.title = "ist größer"
        break

      case RuleComparisonOperator.GreaterOrEqual:
        operatorSpec.title = "ist größer oder gleich"
        break

      case RuleComparisonOperator.In:
        operatorSpec.title = "ist eins von"
        break

      case RuleComparisonOperator.NotIn:
        operatorSpec.title = "ist keins von"
        break

      case RuleComparisonOperator.Changes:
        operatorSpec.title = "ändert sich"
        break
    }

    operatorSpecs.push(operatorSpec)
  }

  return operatorSpecs
}

interface RuleValidationModeSpec {
  mode: RuleValidationMode
  title: string
}

export function getRuleValidationModes(): RuleValidationModeSpec[] {
  const modes: RuleValidationModeSpec[] = []

  for (const [, mode] of Object.entries(RuleValidationMode)) {
    const modeSpec: RuleValidationModeSpec = {
      mode,
      title: mode.toString()
    }

    switch (mode) {
      case RuleValidationMode.MatchAll:
        modeSpec.title = "alle Regeln zutreffen."
        break

      case RuleValidationMode.MatchAny:
        modeSpec.title = "irgendeine Regel zutrifft."
        break
    }

    modes.push(modeSpec)
  }

  return modes
}

interface ActionTypeSpecBase {
  type: ActionType
  title: string

  /**
   * only allow actions for specific option types
   */
  allowedOptionTypes?: readonly ConfiguratorOption["_tag"][]

  /**
   * placeholder text for type value input element
   */
  typeValuePlaceholder?: string
}

interface ActionTypeSpecNo extends ActionTypeSpecBase {
  /**
   * where or not action type value is required
   */
  requireTypeValue: false
}

interface ActionTypeSpecYes extends ActionTypeSpecBase {
  /**
   * where or not action type value is required
   */
  requireTypeValue: true

  /**
   * only allow specific type values
   */
  typeValues: TypeValue[] | null
}

type ActionTypeSpec = ActionTypeSpecYes | ActionTypeSpecNo

interface TypeValue {
  key: string
  value: string
}

interface GetAllActionTypeSpecsOptions {
  action: ConditionAction
  priceTables: readonly Pick<PriceTable, "id" | "name">[]
}

interface GetActionTypeSpecOptions extends GetAllActionTypeSpecsOptions {
  actionType: ActionType
}

// requireTypeValue stays literal so the union narrows; the drift test in
// AppCondition.actionSpec.test.ts guards it against conditionActionSpec.
function getActionTypeSpec(options: GetActionTypeSpecOptions): ActionTypeSpec {
  let listItems: TypeValue[] = []
  if (options.action.option && options.action.option._tag === "list") {
    listItems = (options.action.option as ListOptionType).items.map((item) => {
      return {
        key: item.title,
        value: item.id
      }
    })
  }

  switch (options.actionType) {
    /**
     * base option
     */
    case ActionType.OptionShow:
      return {
        type: options.actionType,
        title: "Anzeigen",
        requireTypeValue: false
      }

    case ActionType.OptionHide:
      return {
        type: options.actionType,
        title: "Nicht anzeigen",
        requireTypeValue: false
      }

    case ActionType.OptionSetValue:
      return {
        type: options.actionType,
        title: "Eingabewert ändern",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Eingabewert",
        typeValues: listItems.length ? listItems : null
      }

    case ActionType.OptionSetDefaultValue:
      return {
        type: options.actionType,
        title: "Standardwert ändern",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Standardwert",
        typeValues: listItems.length ? listItems : null
      }

    case ActionType.OptionAddCustomerNote:
      return {
        type: options.actionType,
        title: "Hinweis anzeigen",
        requireTypeValue: true,
        typeValuePlaceholder: "Text eingeben",
        typeValues: null
      }

    case ActionType.OptionSetReadonly:
      return {
        type: options.actionType,
        title: "Nur lesezugriff",
        requireTypeValue: true,
        typeValues: [
          { key: "Ja", value: "true" },
          { key: "Nein", value: "false" }
        ]
      }

    case ActionType.OptionPriceUpdate:
      return {
        type: options.actionType,
        title: "Preisaufschlag",
        requireTypeValue: true,
        typeValuePlaceholder: "Preisaufschlag angeben",
        typeValues: null
      }

    case ActionType.OptionSetTooltip:
      return {
        type: options.actionType,
        title: "Tooltip ändern",
        requireTypeValue: true,
        typeValuePlaceholder: "Tooltip angeben",
        typeValues: null
      }

    case ActionType.OptionSetFocussedImageUrl:
      return {
        type: options.actionType,
        title: "Focus Bild ändern",
        requireTypeValue: true,
        typeValuePlaceholder: "Focus Bild angeben",
        typeValues: null
      }

    case ActionType.OptionSetPriceTable:
      return {
        type: options.actionType,
        title: "Preistabelle zuweisen",
        requireTypeValue: true,
        typeValuePlaceholder: "Preistabelle auswählen",
        typeValues: options.priceTables.map((priceTable) => {
          return {
            key: priceTable.name,
            value: priceTable.id
          }
        })
      }

    case ActionType.OptionSetPriceFormula:
      return {
        type: options.actionType,
        title: "Preisformel überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neue Preisformel",
        typeValues: null
      }

    /**
     * number option
     */

    case ActionType.NumberOptionSetMinVal:
      return {
        type: options.actionType,
        title: "Minimalwert überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Minimalwert",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: null
      }

    case ActionType.NumberOptionSetMaxVal:
      return {
        type: options.actionType,
        title: "Maximalwert überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Maximalwert",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: null
      }

    case ActionType.NumberOptionSetStepSize:
      return {
        type: options.actionType,
        title: "Schrittgröße überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Schrittgröße",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: null
      }

    case ActionType.NumberOptionSetStepPrice:
      return {
        type: options.actionType,
        title: "Schrittpreis überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neuer Schrittpreis",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: null
      }

    case ActionType.NumberOptionSetStepPriceDescription:
      return {
        type: options.actionType,
        title: "Schrittpreis-Beschreibung überschreiben",
        requireTypeValue: true,
        typeValuePlaceholder: "Neue Beschreibung",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: null
      }

    /**
     * list option
     */

    case ActionType.ListOptionEnableItem:
      return {
        type: options.actionType,
        title: "Listenelement aktivieren",
        requireTypeValue: true,
        typeValuePlaceholder: "Element auswählen",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: listItems
      }

    case ActionType.ListOptionDisableItem:
      return {
        type: options.actionType,
        title: "Listenelement deaktivieren",
        requireTypeValue: true,
        typeValuePlaceholder: "Element auswählen",
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: listItems
      }

    case ActionType.ListOptionChangeItemImage:
      return {
        type: options.actionType,
        title: "Listenelement Bild ändern",
        requireTypeValue: true,
        allowedOptionTypes: conditionActionSpec[options.actionType].allowedOptionTypes ?? undefined,
        typeValues: listItems
      }

    /**
     * subtotal
     */
    case ActionType.SubtotalShow:
      return {
        type: options.actionType,
        title: "Zwischensumme anzeigen",
        requireTypeValue: false
      }
  }
}

export function getAllActionTypeSpecs(
  options: GetAllActionTypeSpecsOptions
): ActionTypeSpec[] {
  const types: ActionTypeSpec[] = []
  for (const [, actionType] of Object.entries(ActionType)) {
    types.push(getActionTypeSpec({ ...options, actionType }))
  }
  return types
}

export function ruleFactory(): ConditionRule {
  return {
    id: StringId.make(),
    targetKind: "option",
    comparisonValue: "",
    comparisonOperator: RuleComparisonOperator.Equal,
    option: null
  }
}

export function actionFactory(): ConditionAction {
  return {
    id: StringId.make(),
    type: ActionType.OptionShow,
    option: null,
    typeValue: null
  }
}

export function conditionFactory(): Condition {
  return {
    id: StringId.make(),
    ruleValidationMode: RuleValidationMode.MatchAll,
    rules: [],
    actions: []
  }
}

export function conditionGroupFactory(): ConditionGroup {
  return {
    id: StringId.make(),
    title: "",
    isEnabled: true,
    note: null,
    conditions: [conditionFactory()]
  }
}

export const GROUP_RULE_OPERATORS: ReadonlySet<RuleComparisonOperator> = new Set([
  RuleComparisonOperator.Equal,
  RuleComparisonOperator.NotEqual,
  RuleComparisonOperator.In,
  RuleComparisonOperator.NotIn
])

export function getRuleComparisonOperatorsFor(target: "option" | "group"): RuleComparisonOperatorSpec[] {
  const all = getRuleComparisonOperators()
  return target === "group" ? all.filter((s) => GROUP_RULE_OPERATORS.has(s.operator)) : all
}
